import React, { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Pencil,
  Plus,
  Printer,
  Trash2,
  X as XIcon,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import QuoteDocument from "./QuoteDocument";
import { findByShortId, pageParam, shortId, apiError, dateIt, eur, inputClass, labelClass, pct, printQuote, quoteNumber, POWER_UNITS, PRODUCT_TYPES } from "./format";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: "quotes", label: "Preventivi" },
  { id: "agents", label: "Commerciali" },
  { id: "products", label: "Prodotti" },
  { id: "suppliers", label: "Listini" },
  { id: "regions", label: "Manodopera" },
  { id: "settings", label: "Impostazioni" },
];

const useAdminFetch = (token) =>
  useCallback(
    (path, opts = {}) =>
      fetch(`${API_URL}/api/admin/${path}`, {
        ...opts,
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": token,
          ...(opts.headers || {}),
        },
      }),
    [token]
  );

const Message = ({ msg }) =>
  msg ? (
    <p
      className={`flex items-center gap-2 text-sm ${
        msg.type === "ok" ? "text-[#0FB36B]" : "text-red-600"
      }`}
    >
      {msg.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg.text}
    </p>
  ) : null;

// -------------------------------------------------------------------
// TABELLA MODIFICABILE GENERICA (commerciali, listino, fornitori, regioni)
// -------------------------------------------------------------------
const cellValue = (col, row, lookups) => {
  const v = row[col.key];
  switch (col.type) {
    case "money":
      return (v === null || v === undefined || v === "") && col.emptyLabel ? col.emptyLabel : eur(v);
    case "pct":
      return v === null || v === undefined || v === "" ? col.emptyLabel || "—" : pct(v);
    case "bool":
      return v ? "Sì" : "No";
    case "select": {
      const opt = (col.options || lookups[col.lookup] || []).find((o) => o.value === v);
      return opt ? opt.label : "—";
    }
    case "multiselect": {
      const opts = col.options || lookups[col.lookup] || [];
      const names = (Array.isArray(v) ? v : [])
        .map((id) => opts.find((o) => o.value === id)?.label)
        .filter(Boolean);
      return names.length ? names.join(", ") : col.emptyLabel || "—";
    }
    case "pin":
      return "••••";
    default:
      return v === "" || v === null || v === undefined ? "—" : String(v);
  }
};

const CellInput = ({ col, value, onChange, lookups, isNew }) => {
  if (col.type === "bool") {
    return (
      <input
        type="checkbox"
        checked={Boolean(value)}
        onChange={(e) => onChange(e.target.checked ? 1 : 0)}
        className="w-4 h-4 accent-[#0FB36B]"
      />
    );
  }
  if (col.type === "multiselect") {
    const options = col.options || lookups[col.lookup] || [];
    const selected = Array.isArray(value) ? value : [];
    const toggle = (id) =>
      onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    return (
      <div className="space-y-1">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
            <input
              type="checkbox"
              checked={selected.includes(o.value)}
              onChange={() => toggle(o.value)}
              className="w-4 h-4 accent-[#0FB36B]"
            />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  if (col.type === "select") {
    const options = col.options || lookups[col.lookup] || [];
    return (
      <select className={inputClass} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        {col.allowEmpty !== false && <option value="">—</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  const numeric = ["money", "number", "pct"].includes(col.type);
  return (
    <input
      type={col.type === "pin" ? "password" : numeric ? "number" : "text"}
      step={col.type === "number" ? "any" : numeric ? "0.01" : undefined}
      className={inputClass}
      value={value ?? ""}
      placeholder={col.type === "pin" ? (isNew ? "Nuovo PIN" : "Lascia vuoto") : col.placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const NO_FILTERS = [];

// Marchio di un prodotto: il nome senza la sigla del modello ("V-Tac IP20" -> "V-Tac",
// "Deye 10kW" -> "Deye"). Extra, backup e colonnine non hanno un marchio.
const BRANDLESS_TYPES = ["extra", "backup", "colonnina"];
const brandOf = (row) =>
  BRANDLESS_TYPES.includes(row.category)
    ? ""
    : String(row.name || "")
        .split(/\s+/)
        .filter((w) => !/\d/.test(w))
        .join(" ");

// filters (opzionale): [{ key, label, allLabel, options: [{value, label}] | (rows) => [...], match(row, value), newValues? }]
// pageSize (opzionale): righe per pagina
const ResourceTable = ({ resource, columns, token, lookups = {}, hint, onChanged, canAdd = true, filters = NO_FILTERS, pageSize }) => {
  const api = useAdminFetch(token);
  const [rows, setRows] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id|null, values }
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api(`quote/${resource}`)
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((json) => setRows(json.data || []))
      .finally(() => setLoading(false));
  }, [api, resource]);

  useEffect(() => {
    load();
    setEditing(null);
    setMsg(null);
  }, [load]);

  const startNew = () => {
    const values = {};
    columns.forEach((c) => {
      values[c.key] = c.default ?? (c.type === "bool" ? 1 : c.type === "multiselect" ? [] : "");
    });
    // Nuovo prodotto con filtri attivi: parte gia' con quei valori (listino, tipo, ...)
    filters.forEach((f) => {
      if (f.newValues && filterValues[f.key]) Object.assign(values, f.newValues(filterValues[f.key]));
    });
    setEditing({ id: null, values });
    setMsg(null);
  };

  const save = async () => {
    const body = { ...editing.values };
    columns.forEach((c) => {
      if (c.type === "pin" && !body[c.key]) delete body[c.key];
    });
    const res = await api(editing.id ? `quote/${resource}/${editing.id}` : `quote/${resource}`, {
      method: editing.id ? "PUT" : "POST",
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      setMsg({ type: "error", text: await apiError(res, "Salvataggio non riuscito.") });
      return;
    }
    setEditing(null);
    setMsg({ type: "ok", text: "Salvato." });
    load();
    onChanged?.();
  };

  const remove = async (row) => {
    if (!window.confirm(`Eliminare "${row.name}"?`)) return;
    const res = await api(`quote/${resource}/${row.id}`, { method: "DELETE" });
    if (!res.ok) {
      setMsg({ type: "error", text: await apiError(res, "Eliminazione non riuscita.") });
      return;
    }
    load();
    onChanged?.();
  };

  const setValue = (key) => (v) =>
    setEditing((s) => ({ ...s, values: { ...s.values, [key]: v } }));

  const activeFilters = filters.filter((f) => filterValues[f.key]);
  const filteredRows = rows.filter((r) => activeFilters.every((f) => f.match(r, filterValues[f.key])));
  const pageCount = pageSize ? Math.max(1, Math.ceil(filteredRows.length / pageSize)) : 1;
  const currentPage = Math.min(page, pageCount);
  const visibleRows = pageSize
    ? filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : filteredRows;

  const editRow = (
    <tr className="bg-[#F4C542]/10">
      {columns.map((c) => (
        <td key={c.key} className="py-2 px-2 align-middle">
          <CellInput
            col={c}
            value={editing?.values[c.key]}
            onChange={setValue(c.key)}
            lookups={lookups}
            isNew={!editing?.id}
          />
        </td>
      ))}
      <td className="py-2 px-2 whitespace-nowrap text-right">
        <button
          type="button"
          onClick={save}
          className="h-9 px-3 rounded-md bg-[#0FB36B] hover:bg-[#0c9a5b] text-white text-xs font-semibold"
        >
          Salva
        </button>
        <button
          type="button"
          onClick={() => setEditing(null)}
          aria-label="Annulla"
          className="ml-1 h-9 w-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );

  return (
    <div className="space-y-3">
      {hint && <p className="text-sm text-gray-500 max-w-3xl">{hint}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {filters.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm text-gray-600">
              {f.label}
              <select
                className={`${inputClass} w-auto`}
                value={filterValues[f.key] || ""}
                onChange={(e) => {
                  setFilterValues((s) => ({ ...s, [f.key]: e.target.value }));
                  setPage(1);
                }}
              >
                <option value="">{f.allLabel}</option>
                {(typeof f.options === "function" ? f.options(rows) : f.options).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          ))}
          {activeFilters.length > 0 && (
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {filteredRows.length} di {rows.length}
              <button
                type="button"
                onClick={() => {
                  setFilterValues({});
                  setPage(1);
                }}
                className="ml-2 underline hover:text-[#0A1F44]"
              >
                Azzera filtri
              </button>
            </span>
          )}
        </div>
        {canAdd && (
          <button
            type="button"
            onClick={startNew}
            disabled={Boolean(editing)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0A1F44] hover:bg-[#0d2855] text-white text-xs font-montserrat font-semibold disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi
          </button>
        )}
      </div>
      <Message msg={msg} />
      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
              {columns.map((c) => (
                <th key={c.key} className="py-3 px-3 font-semibold" style={{ width: c.width }}>
                  {c.label}
                </th>
              ))}
              <th className="py-3 px-3 w-28" />
            </tr>
          </thead>
          <tbody>
            {editing && !editing.id && editRow}
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-6 px-3 text-gray-400">
                  Caricamento…
                </td>
              </tr>
            ) : visibleRows.length === 0 && !editing ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-6 px-3 text-gray-400">
                  Nessun elemento. Clicca "Aggiungi".
                </td>
              </tr>
            ) : (
              visibleRows.map((row) =>
                editing?.id === row.id ? (
                  <React.Fragment key={row.id}>{editRow}</React.Fragment>
                ) : (
                  <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                    {columns.map((c) => (
                      <td key={c.key} className="py-2.5 px-3 text-gray-700">
                        {cellValue(c, row, lookups)}
                      </td>
                    ))}
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        aria-label="Modifica"
                        disabled={Boolean(editing)}
                        onClick={() => {
                          const values = {};
                          columns.forEach((c) => {
                            values[c.key] = c.type === "pin" ? "" : row[c.key] ?? "";
                          });
                          setEditing({ id: row.id, values });
                          setMsg(null);
                        }}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-[#0A1F44] hover:bg-gray-100 disabled:opacity-30"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        aria-label="Elimina"
                        disabled={Boolean(editing)}
                        onClick={() => remove(row)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
          <span>
            {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredRows.length)} di{" "}
            {filteredRows.length}
          </span>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-8 px-3 rounded-md border border-gray-200 bg-white hover:border-[#0A1F44] disabled:opacity-40"
            >
              Precedente
            </button>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`h-8 min-w-[2rem] px-2 rounded-md border ${
                  n === currentPage
                    ? "bg-[#0A1F44] border-[#0A1F44] text-white"
                    : "bg-white border-gray-200 hover:border-[#0A1F44]"
                }`}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage === pageCount}
              className="h-8 px-3 rounded-md border border-gray-200 bg-white hover:border-[#0A1F44] disabled:opacity-40"
            >
              Successiva
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// PREVENTIVI
// -------------------------------------------------------------------
const QUOTES_PAGE_SIZE = 15;

const QuotesPanel = ({ token }) => {
  const api = useAdminFetch(token);
  const [quotes, setQuotes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // Preventivo aperto: /admin?p=quotes.detail&qid=b4ccaa86. "opened" serve per quelli
  // aperti dalla verifica codice, che possono non essere nell'elenco filtrato.
  const [params, setParams] = useSearchParams();
  const qid = pageParam(params, "")[1] === "detail" ? params.get("qid") : null;
  const [opened, setOpened] = useState(null);
  const open = !qid ? null : opened && shortId(opened.id) === qid ? opened : findByShortId(quotes, qid);
  const setOpen = (q) => {
    setOpened(q);
    setParams(q ? { p: "quotes.detail", qid: shortId(q.id) } : { p: "quotes" });
  };

  useEffect(() => {
    api("quote/agents")
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setAgents(j.data || []));
  }, [api]);

  const load = useCallback(() => {
    setLoading(true);
    api(`quotes${agentId ? `?agent_id=${agentId}` : ""}`)
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setQuotes(j.data || []))
      .finally(() => setLoading(false));
  }, [api, agentId]);

  useEffect(load, [load]);

  useEffect(() => {
    setPage(1);
  }, [search, agentId]);

  const needle = search.trim().toLowerCase();
  const filteredQuotes = needle
    ? quotes.filter((q) =>
        [quoteNumber(q), q.number, q.customer?.name, q.agent?.name]
          .filter((v) => v !== null && v !== undefined)
          .some((v) => String(v).toLowerCase().includes(needle))
      )
    : quotes;
  const totalPages = Math.max(1, Math.ceil(filteredQuotes.length / QUOTES_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedQuotes = filteredQuotes.slice(
    (currentPage - 1) * QUOTES_PAGE_SIZE,
    currentPage * QUOTES_PAGE_SIZE
  );

  const remove = async (q) => {
    if (!window.confirm(`Eliminare il preventivo n. ${q.number}?`)) return;
    const res = await api(`quotes/${q.id}`, { method: "DELETE" });
    if (res.ok) load();
  };

  if (open) {
    const c = open.calc || {};
    return (
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 print:hidden">
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#0A1F44]"
          >
            <ArrowLeft className="w-4 h-4" /> Tutti i preventivi
          </button>
          <button
            type="button"
            onClick={() => printQuote(open)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0A1F44] text-white text-sm font-semibold"
          >
            <Printer className="w-4 h-4" /> Stampa / Salva PDF
          </button>
        </div>
        <div className="mb-6 grid sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm print:hidden">
          {[
            ["Materiale", eur(c.material_total)],
            ["Spedizione", eur(c.shipping_total)],
            ["Manodopera", eur(c.labor_total)],
            ["Costo totale", eur(open.cost_total)],
            [`Margine${c.phase ? (c.phase === "tri" ? " trifase" : " monofase") : ""}`, eur(c.margin_amount)],
            [`Provvigione${c.commission_level ? ` ${c.commission_level}` : ""}`, eur(open.commission_amount)],
          ].map(([k, v]) => (
            <div key={k} className="bg-white rounded-lg border border-gray-100 p-3">
              <p className="text-[11px] uppercase tracking-wider text-gray-400">{k}</p>
              <p className="font-semibold text-[#0A1F44]">{v}</p>
            </div>
          ))}
        </div>
        <QuoteDocument quote={open} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-montserrat font-bold text-[#0A1F44] uppercase tracking-wider">
          Tutti i preventivi
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            className={`${inputClass} w-64`}
            placeholder="Cerca per cliente, n. o commerciale"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={`${inputClass} w-auto`}
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            <option value="">Tutti i commerciali</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.code})
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="py-3 px-3">N.</th>
              <th className="py-3 px-3">Data</th>
              <th className="py-3 px-3">Commerciale</th>
              <th className="py-3 px-3">Cliente</th>
              <th className="py-3 px-3 text-right">Costo</th>
              <th className="py-3 px-3 text-right">Totale</th>
              <th className="py-3 px-3 text-right">Provvigione</th>
              <th className="py-3 px-3" />
              <th className="py-3 px-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="py-6 px-3 text-gray-400">
                  Caricamento…
                </td>
              </tr>
            ) : filteredQuotes.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-6 px-3 text-gray-400">
                  {needle ? "Nessun preventivo trovato." : "Nessun preventivo."}
                </td>
              </tr>
            ) : (
              pagedQuotes.map((q) => (
                <tr key={q.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                  <td className="py-2.5 px-3 font-semibold text-[#0A1F44]">
                    <button type="button" onClick={() => setOpen(q)} className="hover:underline">
                      {quoteNumber(q)}
                    </button>
                  </td>
                  <td className="py-2.5 px-3 text-gray-500">{dateIt(q.created_at)}</td>
                  <td className="py-2.5 px-3">{q.agent?.name}</td>
                  <td className="py-2.5 px-3">{q.customer?.name}</td>
                  <td className="py-2.5 px-3 text-right text-gray-500">{eur(q.cost_total)}</td>
                  <td className="py-2.5 px-3 text-right">{eur(q.price_gross)}</td>
                  <td className="py-2.5 px-3 text-right">
                    <span className="font-semibold text-[#0FB36B]">{eur(q.commission_amount)}</span>{" "}
                    {q.input?.commission_level && (
                      <span className="text-xs text-gray-400">({q.input.commission_level})</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3">
                    {q.status === "archiviato" && <span className="text-xs text-gray-400">Archiviata</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      type="button"
                      aria-label="Elimina"
                      onClick={() => remove(q)}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
          <span>
            {filteredQuotes.length} preventivi · pagina {currentPage} di {totalPages}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
              className="h-9 px-3 rounded-md bg-white border border-gray-200 text-[#0A1F44] font-semibold disabled:opacity-40"
            >
              Precedente
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                aria-current={n === currentPage ? "page" : undefined}
                className={`h-9 w-9 rounded-md font-semibold ${
                  n === currentPage
                    ? "bg-[#0A1F44] text-white"
                    : "bg-white border border-gray-200 text-[#0A1F44]"
                }`}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="h-9 px-3 rounded-md bg-white border border-gray-200 text-[#0A1F44] font-semibold disabled:opacity-40"
            >
              Successiva
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// IMPOSTAZIONI
// -------------------------------------------------------------------
const SettingsPanel = ({ token }) => {
  const api = useAdminFetch(token);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api("quote/settings")
      .then((r) => (r.ok ? r.json() : null))
      .then(setForm);
  }, [api]);

  if (!form) return <p className="text-sm text-gray-400">Caricamento…</p>;

  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const res = await api("quote/settings", { method: "PUT", body: JSON.stringify(form) });
    if (res.ok) {
      setForm(await res.json());
      setMsg({ type: "ok", text: "Impostazioni salvate." });
    } else {
      setMsg({ type: "error", text: await apiError(res) });
    }
  };

  const numField = (k, label, help, step = "0.5") => (
    <div>
      <label className={labelClass}>{label}</label>
      <input type="number" step={step} min="0" className={inputClass} value={form[k]} onChange={set(k)} />
      {help && <p className="text-[11px] text-gray-400 mt-1">{help}</p>}
    </div>
  );

  const groupTitle = (t) => (
    <p className="text-xs font-montserrat font-bold text-[#0A1F44] uppercase tracking-wider pt-2">{t}</p>
  );

  return (
    <form onSubmit={save} className="bg-white rounded-xl border border-gray-100 p-6 max-w-2xl space-y-5">
      {groupTitle("Margine fisso")}
      <div className="grid sm:grid-cols-2 gap-5">
        {numField("margin_mono", "Impianto monofase €", null, "50")}
        {numField("margin_tri", "Impianto trifase €", null, "50")}
      </div>
      {groupTitle("Manodopera retrofit")}
      <div className="grid sm:grid-cols-2 gap-5">
        {numField("retrofit_labor_mono", "Retrofit monofase €", "Solo inverter e batterie su impianto esistente (al posto dei €/kW della regione)", "50")}
        {numField("retrofit_labor_tri", "Retrofit trifase €", null, "50")}
      </div>
      {groupTitle("Provvigione commerciale")}
      <div className="grid sm:grid-cols-4 gap-5">
        {numField("commission_alta", "Alta €", null, "50")}
        {numField("commission_media", "Media €", null, "50")}
        {numField("commission_bassa", "Bassa €", null, "50")}
        {numField("commission_free_max", "Libera max €", "Personalizzabile per commerciale", "50")}
      </div>
      {groupTitle("Generale")}
      <div className="grid sm:grid-cols-2 gap-5">
        {numField("vat_pct", "IVA %")}
        {numField("validity_days", "Validità offerta (giorni)", null, "1")}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>IBAN Energeide per i bonifici</label>
          <input className={`${inputClass} font-mono`} value={form.company_iban} onChange={set("company_iban")} />
        </div>
        <div>
          <label className={labelClass}>Findomestic tasso zero (mesi:maggiorazione %)</label>
          <input className={`${inputClass} font-mono`} value={form.finance_rates} onChange={set("finance_rates")} />
          <p className="text-[11px] text-gray-400 mt-1">Es. 12:3.96, 24:7.42, 36:10.72</p>
        </div>
      </div>
      {groupTitle("Findomestic con interessi")}
      <div className="grid sm:grid-cols-4 gap-5">
        {numField("finance_tan", "TAN %", null, "0.05")}
        {numField("finance_fee", "Spese istruttoria €", null, "10")}
        {numField("finance_sir", "Incasso rata €", null, "0.5")}
        <div>
          <label className={labelClass}>Durate (mesi)</label>
          <input className={`${inputClass} font-mono`} value={form.finance_months} onChange={set("finance_months")} />
        </div>
      </div>
      <div>
        <label className={labelClass}>Note in fondo al preventivo</label>
        <textarea
          rows={3}
          className={`${inputClass} h-auto py-2`}
          value={form.footer_notes}
          onChange={set("footer_notes")}
        />
      </div>
      <Message msg={msg} />
      <button
        type="submit"
        className="h-11 px-6 rounded-lg bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] font-montserrat font-bold text-sm"
      >
        Salva impostazioni
      </button>
    </form>
  );
};

// -------------------------------------------------------------------
// CONTENITORE
// -------------------------------------------------------------------
const QuoteAdmin = ({ token }) => {
  // Scheda aperta nell'indirizzo: /admin?p=quotes, /admin?p=quotes.products, ...
  const [params, setParams] = useSearchParams();
  const subPage = pageParam(params, "quotes")[1];
  const section = SECTIONS.some((s) => s.id === subPage) ? subPage : "quotes";
  const setSection = (id) => setParams({ p: id === "quotes" ? "quotes" : `quotes.${id}` });
  const [suppliers, setSuppliers] = useState([]);
  const api = useAdminFetch(token);

  const loadSuppliers = useCallback(() => {
    api("quote/suppliers")
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setSuppliers((j.data || []).map((s) => ({ value: s.id, label: s.name }))));
  }, [api]);

  useEffect(loadSuppliers, [loadSuppliers]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6 print:hidden">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSection(s.id)}
            className={`h-9 px-4 rounded-full text-sm font-montserrat font-semibold border transition-colors ${
              section === s.id
                ? "bg-[#0FB36B] border-[#0FB36B] text-white"
                : "bg-white border-gray-200 text-gray-500 hover:text-[#0A1F44]"
            }`}
          >
            {s.label}
          </button>
        ))}
        <a
          href="/preventivatore"
          target="_blank"
          rel="noreferrer"
          className="ml-auto self-center text-sm text-[#0FB36B] hover:underline"
        >
          Apri area commerciali ↗
        </a>
      </div>

      {section === "quotes" && <QuotesPanel token={token} />}

      {section === "agents" && (
        <ResourceTable
          resource="agents"
          token={token}
          columns={[
            { key: "name", label: "Nome" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Telefono" },
            { key: "code", label: "Sigla", width: 80, placeholder: "MR" },
            { key: "pin", label: "PIN", type: "pin", width: 120 },
            {
              key: "max_commission_eur",
              label: "Provv. libera max €",
              type: "money",
              width: 130,
              emptyLabel: "Generale",
              placeholder: "Generale",
            },
            { key: "active", label: "Attivo", type: "bool", width: 70 },
          ]}
        />
      )}

      {section === "products" && (
        <ResourceTable
          resource="products"
          token={token}
          lookups={{ suppliers }}
          pageSize={10}
          filters={[
            {
              key: "listino",
              label: "Listino",
              allLabel: "Tutti i listini",
              options: suppliers,
              match: (row, v) => !row.listino_ids?.length || row.listino_ids.includes(v),
              newValues: (v) => ({ listino_ids: [v] }),
            },
            {
              key: "category",
              label: "Tipo",
              allLabel: "Tutti i tipi",
              options: Object.entries(PRODUCT_TYPES).map(([value, label]) => ({ value, label })),
              match: (row, v) => row.category === v,
              newValues: (v) => ({ category: v }),
            },
            {
              key: "brand",
              label: "Marchio",
              allLabel: "Tutti i marchi",
              options: (rows) =>
                [...new Set(rows.map(brandOf).filter(Boolean))]
                  .sort((a, b) => a.localeCompare(b, "it"))
                  .map((name) => ({ value: name, label: name })),
              match: (row, v) => brandOf(row) === v,
              newValues: (v) => ({ name: v }),
            },
          ]}
          columns={[
            {
              key: "category",
              label: "Tipo",
              type: "select",
              allowEmpty: false,
              default: "modulo",
              options: Object.entries(PRODUCT_TYPES).map(([value, label]) => ({ value, label })),
            },
            { key: "name", label: "Prodotto" },
            { key: "power_kw", label: "Potenza", type: "number", width: 100 },
            {
              key: "power_unit",
              label: "Unità",
              type: "select",
              allowEmpty: false,
              default: "kW",
              width: 90,
              options: POWER_UNITS.map((u) => ({ value: u, label: u })),
            },
            { key: "listino_ids", label: "Listini", type: "multiselect", lookup: "suppliers", emptyLabel: "Tutti" },
            { key: "material_cost", label: "Costo a pezzo", type: "money", width: 130 },
            { key: "active", label: "Attivo", type: "bool", width: 70 },
          ]}
        />
      )}

      {section === "suppliers" && (
        <ResourceTable
          resource="suppliers"
          token={token}
          onChanged={loadSuppliers}
          columns={[
            { key: "name", label: "Listino" },
            {
              key: "shipping_type",
              label: "Tipo spedizione",
              type: "select",
              allowEmpty: false,
              default: "fixed",
              options: [
                { value: "fixed", label: "Importo fisso €" },
                { value: "percent", label: "Percentuale %" },
                { value: "per_module", label: "€ per pannello" },
              ],
            },
            { key: "shipping_value", label: "Valore", type: "number", width: 110 },
            { key: "notes", label: "Note" },
          ]}
        />
      )}

      {section === "regions" && (
        <ResourceTable
          resource="regions"
          token={token}
          canAdd={false}
          columns={[
            { key: "name", label: "Regione" },
            { key: "labor_per_kw", label: "€ per kW", type: "money", width: 130 },
            { key: "labor_fixed", label: "Quota fissa €", type: "money", width: 130 },
          ]}
        />
      )}

      {section === "settings" && <SettingsPanel token={token} />}
    </div>
  );
};

export default QuoteAdmin;
